import { Fragment, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { api } from "@/api/client";
import { getWorkspace, isWorkspace, setWorkspace, useWorkspace } from "@/api/workspace";
import type { Workspace } from "@/api/workspace";
import { MkLayout } from "@/mk/MkLayout";
import { PmGlobalSearch } from "@/pm/PmGlobalSearch";
import { PmWorkspaceToggle } from "@/pm/PmWorkspaceToggle";

import { useAuth } from "./auth";
import { fetchWorkspaceContext } from "./workspaceContext";
import type { WorkspaceContext as WorkspaceContextData } from "./workspaceContext";
import { APPS, activeFor, navTree, navigationQuery, recordRoute, routeFor, sectionAllowed,
         trailFor } from "./navigation";
import { usePageTrail } from "./pageTrail";
import type { NavAccess } from "./navigation";
import { activeOwner, allWork, onWorkChanged, projectTemplates, shellCache } from "@/offline/store";
import { pageList } from "./pageSearch";
import { SearchPagesContext } from "./searchPages";

export type WorkspaceContext = WorkspaceContextData;

export function useNavAccess() {
    const { user } = useAuth();
    const workspace = useWorkspace();
    return useQuery<{ data: NavAccess }>({
        queryKey: ["navigation", user?.id, user?.company?.id, workspace],
        queryFn: async () => {
            if (!navigator.onLine) {
                const owner = await activeOwner();
                const scoped = owner?.workspace === getWorkspace() && owner.user_id === user?.id && owner.company_id === user.company?.id;
                const saved = scoped ? await shellCache<NavAccess>("navigation") : undefined;
                if (saved) return { data: saved };
                if (owner && scoped) {
                    const works = (await allWork(owner)).filter(item => item.base.owner.company_id === owner.company_id
                        && item.base.owner.workspace === owner.workspace);
                    const boq = works.some(item => item.base.kind === "boq") || (await projectTemplates("boq")).length > 0;
                    const timeline = works.some(item => item.base.kind === "timeline") || (await projectTemplates("timeline")).length > 0;
                    const apps = Object.fromEntries(APPS.map(app => [app.tag,
                        app.id === "boq" ? boq : app.id === "timeline" ? timeline : false]));
                    const denied = { read: false, write: false, create: false, unlink: false };
                    const models = Object.fromEntries(APPS.flatMap(app => app.sections.flatMap(section =>
                        section.model ? [[section.model, denied] as const] : [])));
                    if (boq) models["bill.of.quantities"] = { read: true, write: false, create: false, unlink: false };
                    return { data: { apps, models, groups: {}, boards: {} } };
                }
                throw new Error("Offline navigation is not available");
            }
            return api.raw<NavAccess>(navigationQuery());
        },
        networkMode: "always",
        staleTime: 5 * 60 * 1000,
    });
}

const KEPT_QUERIES = new Set(["navigation", "workspace"]);

export function useWorkspaceContext() {
    const { user } = useAuth();
    const workspace = useWorkspace();
    return useQuery<WorkspaceContext>({
        queryKey: ["workspace", "context", user?.id, user?.company?.id, workspace],
        queryFn: () => fetchWorkspaceContext(user?.id && user.company?.id
            ? { userId: user.id, companyId: user.company.id } : undefined),
        networkMode: "always",
    });
}

export function Shell({ children }: { children: ReactNode }) {
    const { t } = useTranslation();
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const access = useNavAccess();
    const context = useWorkspaceContext();
    const queryClient = useQueryClient();
    const workspace = useWorkspace();

    useEffect(() => {
        if (!user || !user.company || !navigator.onLine) return;
        const companyId = user.company.id;
        const save = async () => {
            const owner = await activeOwner();
            if (!owner || owner.user_id !== user.id || owner.company_id !== companyId || owner.workspace !== workspace) return;
            const identity = { userId: user.id, companyId };
            if (access.data?.data) await shellCache("navigation", { ...identity, value: access.data.data });
            if (context.data) await shellCache("context", { ...identity, value: context.data });
        };
        void save().catch(() => {});
        return onWorkChanged(() => { void save().catch(() => {}); });
    }, [user?.id, user?.company?.id, workspace, access.data, context.data]);

    const isHome = location.pathname === "/";
    const active = isHome ? undefined : activeFor(
        location.pathname, location.search,
        (_app, section) => sectionAllowed(access.data?.data, section,
                                          context.data?.is_owner_org),
        workspace);
    const activeApp = active?.split(":")[0];

    const items = useMemo(
        () => (access.data
            ? navTree(t, access.data.data, activeApp, context.data?.is_owner_org, workspace)
            : []),
        [t, access.data, activeApp, context.data?.is_owner_org, workspace]);

    const pageTrail = usePageTrail();
    const trail = useMemo(() => [...trailFor(t, active), ...pageTrail], [t, active, workspace, pageTrail]);

    // Built from the very tree the sidebar draws, and only after the server
    // has answered: no verdict means no page is offered.
    const pages = useMemo(
        () => (access.data
            ? pageList(t, access.data.data, context.data?.is_owner_org, workspace)
            : []),
        [t, access.data, context.data?.is_owner_org, workspace]);

    function applyWorkspace(next: Workspace) {
        setWorkspace(next);
        queryClient.removeQueries({
            predicate: (query) => !KEPT_QUERIES.has(String(query.queryKey[0])),
        });
    }

    // a list of projects, alone or inside a portfolio, names the workspace it shows
    const routeType = location.pathname === "/projects" || location.pathname === "/portfolios"
        ? new URLSearchParams(location.search).get("type") : null;

    useEffect(() => {
        if (isWorkspace(routeType) && routeType !== workspace) {
            applyWorkspace(routeType);
        }
    }, [routeType]);

    function switchWorkspace(next: Workspace) {
        if (next === workspace) {
            return;
        }
        applyWorkspace(next);
        if (location.pathname === "/projects") {
            navigate(`/projects?type=${next}`);
        } else if (location.pathname === "/portfolios") {
            const search = new URLSearchParams(location.search);
            search.set("type", next);
            navigate(`/portfolios?${search.toString()}`);
        } else if (!isHome && !activeFor(location.pathname, location.search, undefined, next)) {
            navigate("/");
        }
    }

    return (
        <SearchPagesContext.Provider value={pages}>
            <div className={`pm-app${isHome ? " pm-home-app" : ""}`}>
                <MkLayout
                    sidebarSection={activeApp ?? "home"}
                    brand={{ label: t("nav.home"), icon: "fa-home", onClick: () => navigate("/") }}
                    personal={{
                        name: user?.name ?? "",
                        status: context.data?.presence ?? "offline",
                        avatarUrl: user ? `/api/v1/files/image/res.users/${user.id}/avatar_128` : null,
                    }}
                    items={items}
                    active={active}
                    trail={trail}
                    breadcrumb={!isHome}
                    sidebarExtra={<PmWorkspaceToggle value={workspace} onChange={switchWorkspace} />}
                    header={isHome ? undefined : <PmGlobalSearch />}
                    onSelect={(id) => {
                        if (id.startsWith("crumb:")) {
                            // the application's own crumb leads back to its first page
                            const app = APPS.find((entry) => id === `crumb:${active}`
                                && active?.split(":")[0] === entry.id);
                            if (app && pageTrail.length) {
                                navigate(app.route);
                            }
                            return;
                        }
                        const route = routeFor(id, workspace, (section) => sectionAllowed(
                            access.data?.data, section, context.data?.is_owner_org));
                        if (route) {
                            navigate(route);
                        }
                    }}
                    onOpenRecord={(model, id) => {
                        const route = recordRoute(model, id);
                        if (route) {
                            navigate(route);
                        }
                    }}
                    onLogout={() => void signOut()}
                >
                    {isHome ? <Fragment key={workspace}>{children}</Fragment> : (
                        <div className="pm-page-swap" key={`${workspace}:${active ?? location.pathname}`}>
                            {children}
                        </div>
                    )}
                </MkLayout>
            </div>
        </SearchPagesContext.Provider>
    );
}
